import cn from 'classnames';
import { ChangeEventHandler, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';
import { Typography } from '@react-md/typography';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { UserPurchaseTemplate } from '@services/df/inventory';
import { inventoryPageActions, selectInventorySelectedIds } from '@modules/inventory-page';
import { Fade, FADE_STAGGER } from '@components/animations';
import { Label } from '@components/label';
import { InventoryPart } from './inventory-part';

import styles from './inventory-page.module.scss';

export const InventoryBody = ({ data }: { data: UserPurchaseTemplate[] }) => {
    const dispatch = useAppDispatch();
    const selectedInventoryIds = useAppSelector(selectInventorySelectedIds);

    const handleChangeCheckbox: ChangeEventHandler<HTMLInputElement> = useCallback(
        ({ target: { value: id, checked } }) => {
            if (checked) {
                dispatch(inventoryPageActions.selectedIdsChanged([...selectedInventoryIds, id]));
            } else {
                dispatch(inventoryPageActions.selectedIdsChanged(selectedInventoryIds.filter(_id => _id !== id)));
            }
        },
        [dispatch, selectedInventoryIds],
    );

    const { t } = useTranslation();

    return !data.length ? (
        <Typography type="body-1" component="div" className={cn('base-paddings', 'no-top')}>
            {t('messages.noMatchingResults', 'No matching results')}
        </Typography>
    ) : (
        <Fade
            in
            className={cn('overflow-x-auto', styles.table)}
            mountOnEnter
            // unmountOnExit
        >
            <table>
                <thead className={styles.head}>
                    <tr>
                        <td className={cn(styles.cell, styles.checkbox)} />
                        <td className={styles.cell}>
                            <Label className={styles.label}>{t('labels.productModel', 'Model')}</Label>
                        </td>
                        <td className={styles.cell}>
                            <Label className={styles.label}>{t('labels.material', 'Material')}</Label>
                        </td>
                        <td className={styles.cell}>
                            <Label className={styles.label}>{t('labels.productSpecification', 'Specification')}</Label>
                        </td>
                        <td className={styles.cell}>
                            <Label className={cn(styles.label, 'rmd-typography--center')}>
                                {t('labels.priorityDays', 'Priority days')}
                            </Label>
                        </td>
                        <td className={styles.cell}>
                            <Label className={cn(styles.label, 'rmd-typography--center')}>
                                {t('labels.quantity', 'Quantity')}
                            </Label>
                        </td>
                        <td className={styles.cell}>
                            <Label className={cn(styles.label, 'rmd-typography--right')}>
                                {t('labels.productPrice', 'Price')}
                            </Label>
                        </td>
                    </tr>
                </thead>

                <TransitionGroup component="tbody" className={styles.body} enter={false} exit={false}>
                    {data?.map((part, i) => {
                        const partId = part.id.toString();
                        return (
                            <InventoryPart
                                key={partId}
                                part={part}
                                onChangeCheckbox={handleChangeCheckbox}
                                checked={selectedInventoryIds.includes(partId)}
                                delay={FADE_STAGGER * (i + 1)}
                            />
                        );
                    })}
                </TransitionGroup>
            </table>
        </Fade>
    );
};
